import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

// Rec-4 · Topbar notification bell. Polls the unread count, opens a dropdown of recent
// notifications (own + broadcast), marks items read, and deep-links to a notification's
// target screen. Backed by api/notifications (unread-count / list / mark-read /
// mark-all-read). Self-contained standalone component — dropped into the app topbar.
interface Notif {
  notificationID: number; type: string; title: string; message: string;
  link: string | null; severity: string | null; relatedModule: string | null;
  isRead: boolean; createdAt: string;
}

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative">
      <button (click)="toggle()" title="Notifications"
        class="relative w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-brand hover:border-brand/40 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        <span *ngIf="unread() > 0"
          class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
          {{ unread() > 99 ? '99+' : unread() }}
        </span>
      </button>

      <!-- Backdrop closes the dropdown -->
      <div *ngIf="open()" class="fixed inset-0 z-40" (click)="close()"></div>

      <div *ngIf="open()"
        class="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-surface border border-slate-200 rounded-xl shadow-pop z-50">
        <div class="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 sticky top-0 bg-surface">
          <span class="text-[13px] font-semibold text-slate-800">Notifications</span>
          <button *ngIf="items().length" (click)="markAllRead()" class="text-[11px] text-brand font-medium hover:underline">Mark all read</button>
        </div>

        <div *ngIf="loading()" class="px-4 py-6 text-center text-[13px] text-secondary">Loading…</div>
        <div *ngIf="!loading() && !items().length" class="px-4 py-8 text-center text-[13px] text-secondary">You're all caught up.</div>

        <button *ngFor="let n of items()" (click)="onClick(n)"
          class="w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-canvas transition-colors flex gap-3"
          [class.bg-brand-50]="!n.isRead">
          <span class="mt-1 w-2 h-2 rounded-full shrink-0"
            [ngClass]="{
              'bg-rose-500': n.severity === 'high' || n.severity === 'danger',
              'bg-amber-500': n.severity === 'medium' || n.severity === 'warning',
              'bg-info': !n.severity || (n.severity !== 'high' && n.severity !== 'danger' && n.severity !== 'medium' && n.severity !== 'warning')
            }"></span>
          <span class="min-w-0">
            <span class="block text-[13px] font-medium text-slate-800 truncate">{{ n.title }}</span>
            <span class="block text-[12px] text-secondary line-clamp-2">{{ n.message }}</span>
            <span class="block text-[10.5px] text-secondary mt-0.5">{{ n.createdAt | date:'dd MMM, HH:mm' }}</span>
          </span>
        </button>

        <button (click)="viewAll()"
          class="block w-full px-4 py-2.5 text-center text-[12px] text-brand font-medium hover:bg-canvas border-t border-slate-100">View all alerts</button>
      </div>
    </div>
  `
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  unread = signal(0);
  open = signal(false);
  loading = signal(false);
  items = signal<Notif[]>([]);
  private timer: any = null;
  private readonly base = '/api/notifications';

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.refreshCount();
    // Poll the unread count every 45s so the badge stays current without a page reload.
    this.timer = setInterval(() => this.refreshCount(), 45000);
  }
  ngOnDestroy(): void { if (this.timer) clearInterval(this.timer); }

  private refreshCount(): void {
    this.http.get<{ _obj: { count: number } }>(`${this.base}/unread-count`).subscribe({
      next: r => this.unread.set(r._obj?.count ?? 0),
      error: () => { /* silent — bell simply shows no badge if the endpoint is unavailable */ }
    });
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    if (next) this.loadList();
  }
  close(): void { this.open.set(false); }
  viewAll(): void { this.close(); this.router.navigateByUrl('/hrm/alerts'); }

  private loadList(): void {
    this.loading.set(true);
    this.http.get<{ _obj: Notif[] }>(`${this.base}/list/false`).subscribe({
      next: r => { this.items.set(r._obj ?? []); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  onClick(n: Notif): void {
    if (!n.isRead) {
      this.http.post(`${this.base}/mark-read/${n.notificationID}`, {}).subscribe({
        next: () => { this.refreshCount(); n.isRead = true; }
      });
    }
    if (n.link) { this.close(); this.router.navigateByUrl(n.link); }
  }

  markAllRead(): void {
    this.http.post(`${this.base}/mark-all-read`, {}).subscribe({
      next: () => { this.items.update(list => list.map(x => ({ ...x, isRead: true }))); this.unread.set(0); }
    });
  }
}
